import { componentDefinitions } from "./components";
import { createCustomBackend } from "../../api/adapters/custom-backend";
import { localBackend } from "../../api/adapters/local-backend";

export function getSelectedBackend() {
    const isDev = Boolean((import.meta as any)?.env?.DEV || import.meta?.url?.includes("localhost"));
    if (isDev) {
        return localBackend;
    }
    return createCustomBackend({ apiBaseUrl: "/api/easyblocks" });
}

// Order components in the editor: Primitives → UI → Sections → External (root layout last).
const componentGroupById: Record<string, "primitives" | "ui" | "sections" | "external" | "layout"> = {
    // Layout (root)
    RootLayoutSections: "layout",

    // Primitives
    HeadingBlock: "primitives",
    QuillText: "primitives",
    ButtonLink: "primitives",
    DocumentLink: "primitives",
    ImageBlock: "primitives",

    // UI
    PhotoAlbum: "sections",
    AccordionItem: "ui",
    Card: "ui",

    // Sections
    HeroSection: "sections",
    HeroImageSection: "sections",
    CTASection: "sections",
    SingleColumn: "sections",
    GridSection: "sections",
    CardsSection: "sections",
    ColumnsSection: "sections",
    AccordionSection: "sections",

    // External
    // NewsCard: "external",
    NewsLatestRow: "external",
    // EventCard: "external",
    EventsRow: "external",
    RosterAccordion: "external",
    NeighborhoodsAccordion: "external",
    ResidentDirectorySection: "external",
    DocumentsAccordion: "external",
    ListTemplateSection: "external",
};

const groupRank: Record<"primitives" | "ui" | "sections" | "external" | "layout", number> = {
    primitives: 0,
    ui: 1,
    sections: 2,
    external: 3,
    layout: 4,
};

const orderedComponentDefinitions = [...componentDefinitions].sort((a, b) => {
    const ga = componentGroupById[a.id] ?? "ui";
    const gb = componentGroupById[b.id] ?? "ui";
    const ra = groupRank[ga] ?? 999;
    const rb = groupRank[gb] ?? 999;
    if (ra !== rb) return ra - rb;
  if (ga === "external" && gb === "external") {
    const externalOrder: Record<string, number> = {
      NewsLatestRow: 0,
      EventsRow: 1,
      PhotoAlbum: 2,
      RosterAccordion: 3,
      NeighborhoodsAccordion: 4,
      ResidentDirectorySection: 5,
      DocumentsAccordion: 6,
      ListTemplateSection: 7,
    };
    const ea = externalOrder[a.id] ?? 999;
    const eb = externalOrder[b.id] ?? 999;
    if (ea !== eb) return ea - eb;
  }
    // Stable-ish ordering within group: keep existing order by comparing indices in the original array.
    return componentDefinitions.findIndex((x) => x.id === a.id) - componentDefinitions.findIndex((x) => x.id === b.id);
});

// Full Easyblocks config object
export const easyblocksConfig = {
    backend: getSelectedBackend(),
    locales: [{ code: "en-US", isDefault: true }],
    components: orderedComponentDefinitions,
    // Non-standard config field read by our patched editor build.
    // Forces the editor to open in fit-screen mode while the top bar stays hidden.
    editorViewport: "fit-screen",
    // Hide all built-in device options; fit-screen is the only supported editor viewport.
    // https://docs.easyblocks.io/essentials/configuration#config.devices
    devices: {
        xs: { hidden: true },
        sm: { hidden: true },
        md: { hidden: true },
        lg: { hidden: true },
        xl: { hidden: true },
        "2xl": { hidden: true },
    },
    tokens: {
        colors: [{ id: "default", label: "Default", value: "#212529", isDefault: true }],
        fonts: [
            {
                id: "default",
                label: "Default",
                value: {
                    fontFamily: "inherit",
                    fontSize: 16,
                    fontWeight: 400,
                    lineHeight: 1.6,
                },
                isDefault: true,
            },
        ],
        space: [{ id: "0", label: "None", value: "0px", isDefault: true }],
    },
    types: {
        news: {
            type: "external",
            widgets: [{ id: "news", label: "News items" }],
        },
        events: {
            type: "external",
            widgets: [{ id: "events", label: "Events" }],
        },
        media: {
            type: "inline",
            widget: { id: "media", label: "Media" },
            defaultValue: "https://placehold.co/400x400",
        },
        document: {
            type: "inline",
            widget: { id: "document", label: "Document" },
            defaultValue: {},
        },
        documentCategory: {
            type: "inline",
            widget: { id: "documentCategory", label: "Document category" },
            defaultValue: 0,
        },
        documentYear: {
            type: "inline",
            widget: { id: "documentYear", label: "Document year" },
            defaultValue: 0,
        },
        album: {
            type: "inline",
            widget: { id: "album", label: "Album" },
            defaultValue: 0,
        },
        rosterRowFilter: {
            type: "inline",
            widget: { id: "rosterRowFilter", label: "Row Filter" },
            defaultValue: "all",
        },
        backgroundColor: {
            type: "inline",
            widget: { id: "backgroundColor", label: "Background Color" },
            defaultValue: "text-bg-white",
        },
        buttonColor: {
            type: "inline",
            widget: { id: "buttonColor", label: "Button Color" },
            defaultValue: "primary",
        },
        borderColor: {
            type: "inline",
            widget: { id: "borderColor", label: "Border Color" },
            defaultValue: "no-class",
        },
        textColor: {
            type: "inline",
            widget: { id: "textColor", label: "Text Color" },
            defaultValue: "no-class",
        },
    },
    hideCloseButton: true,
};
